import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import { API } from '../api';
import { AdminProgramItem, GroupInterface } from '../pages/new-designs/tabs/Program/utils/programTypes';
import { getProgramTypeLabel } from '../pages/new-designs/tabs/Program/utils/programStatus';
import { getProgramCreatorFromGroupOrProgramDetails } from '../pages/new-designs/utils/programCreatorDisplay';

interface Args {
  orgId: string;
  enabled: boolean;
}

/**
 * Stable stand-in for "no programs yet". The query is disabled until the org id
 * and both tokens are known, and a disabled or errored query reports
 * `data: undefined` with `isLoading: false`. A `= []` default at the call site
 * would then mint a fresh array on every render, and any memo or effect keyed on
 * it would re-run forever — so consumers default to this shared constant.
 */
export const EMPTY_ADMIN_PROGRAMS: GroupInterface[] = [];

const readTokens = () => {
  if (typeof window === 'undefined') return { idToken: '', accessToken: '' };
  try {
    const raw = localStorage.getItem('tokens');
    const parsed = raw ? JSON.parse(raw) : {};
    return { idToken: parsed?.id_token || '', accessToken: parsed?.access_token || '' };
  } catch {
    return { idToken: '', accessToken: '' };
  }
};

const mapAdminProgram =
  (orgId: string) =>
  (item: AdminProgramItem): GroupInterface => {
    const { name, avatar } = getProgramCreatorFromGroupOrProgramDetails(item);
    return {
      grpId: item.id,
      name: item.name,
      status: item.status,
      type: item.type,
      orgId,
      creatorName: name,
      avatar: avatar || '',
      typeLabel: getProgramTypeLabel(item.type),
      description: item.buildState?.description,
      buildState: item.buildState,
      creator: item.creator,
      createdBy: item.createdBy,
      aimdBuildSeconds: item.aimdBuildSeconds ?? 0,
      aimdCreateSeconds: item.aimdCreateSeconds ?? null
    };
  };

const useAdminPrograms = ({ orgId, enabled }: Args) => {
  const { idToken, accessToken } = readTokens();
  const isReady = enabled && Boolean(orgId && idToken && accessToken);

  return useQuery({
    queryKey: [queryKeys.GET_ADMIN_PROGRAMS, orgId],
    queryFn: async () => {
      const response = await API.getAdminProgramList({
        orgId,
        idtoken: idToken,
        authorization: accessToken
      });
      const items: AdminProgramItem[] = response?.data?.data || [];
      return items.map(mapAdminProgram(orgId));
    },
    enabled: isReady,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  });
};

export default useAdminPrograms;
